import { supabase } from './supabase';
import { isApprovedProfile } from './profileAccess';
import { withTimeout } from './withTimeout';
import { UserContext, UserRole, SecurityConfig } from './types';
import { Tables } from './database.types';

// User context cache to prevent repeated database queries
let userContextCache: { [userId: string]: { context: UserContext; timestamp: number } } = {};
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache
let sessionGeneration = 0;
let observedActor: string | null | undefined;
let authObservation = 0;
let listeningForAuth = false;
type ContextFlight = { generation: number; result: Promise<UserContext | null>; pending: number; finished: boolean };
let contextFlight: ContextFlight | null = null;

function releaseDrainedFlight(flight: ContextFlight) {
  if (contextFlight === flight && flight.finished && flight.pending === 0) contextFlight = null;
}

function trackContextOperation<T>(flight: ContextFlight, operation: PromiseLike<T>): Promise<T> {
  flight.pending++;
  // A caller deadline does not cancel GoTrue's SDK lock or its queued work.
  // Retain the flight until the actual operation settles, even after timeout.
  return Promise.resolve(operation).finally(() => {
    flight.pending--;
    releaseDrainedFlight(flight);
  });
}

function listenForContextAuthChanges() {
  if (listeningForAuth) return;
  supabase.auth.onAuthStateChange((event, session) => {
    const actor = session?.user?.id || null;
    if (event === 'INITIAL_SESSION') {
      if (observedActor === undefined) observedActor = actor;
      return;
    }
    const benignRefresh = (event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN') &&
      actor !== null && (observedActor === undefined || actor === observedActor);
    observedActor = actor;
    authObservation++;
    if (!benignRefresh) sessionGeneration++;
    userContextCache = {};
  });
  listeningForAuth = true;
}

// Security configuration
const SECURITY_CONFIG: SecurityConfig = {
  enableRoleBasedAccess: true,
  bypassForAdmin: true,
  logAccessAttempts: true,
};

/**
 * Get current authenticated user context with role and property relationships
 * Uses caching to prevent repeated database queries
 */
export async function getCurrentUserContext(): Promise<UserContext | null> {
  listenForContextAuthChanges();
  if (!contextFlight) {
    const flight: ContextFlight = { generation: sessionGeneration, result: Promise.resolve(null), pending: 0, finished: false };
    contextFlight = flight;
    flight.result = readCurrentUserContext(flight).finally(() => {
      flight.finished = true;
      releaseDrainedFlight(flight);
    });
  }
  const flight = contextFlight;
  // Do not pile a new account's requests behind an old, non-cancellable SDK call.
  if (flight.generation !== sessionGeneration) return null;
  const result = await flight.result;
  return flight.generation === sessionGeneration ? result : null;
}

async function readCurrentUserContext(flight: ContextFlight, refreshAttempt = 0): Promise<UserContext | null> {
  const currentGeneration = () => flight.generation === sessionGeneration;
  const bounded = <T>(operation: PromiseLike<T>, ms: number, message: string) =>
    withTimeout(trackContextOperation(flight, operation), ms, message);
  const boundedRead = <T>(query: (signal: AbortSignal) => PromiseLike<T>, message: string) => {
    const controller = new AbortController();
    // Cancel only context reads; a transport ignoring abort must not retain the flight.
    return trackContextOperation(flight,
      withTimeout(Promise.resolve(query(controller.signal)), 10000, message)
        .finally(() => controller.abort()));
  };
  try {
    // Get current authenticated user from Supabase
    const { data: { user }, error: authError } = await bounded(supabase.auth.getUser(), 10000, 'Authentication check timed out');
    
    if (!currentGeneration() || authError || !user) {
      return null;
    }
    if (observedActor !== undefined && observedActor !== user.id) return null;
    observedActor = user.id;

    // Refresh privilege state before consulting the relationship cache.
    const profileObservation = authObservation;
    const refreshChangedProfile = () => refreshAttempt < 1 && currentGeneration()
      ? readCurrentUserContext(flight, refreshAttempt + 1) : Promise.resolve(null);
    const { data: initialProfile, error: profileError } = await boundedRead(signal => supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .abortSignal(signal)
      .single(), 'Profile check timed out');
    const profile = initialProfile;
    if (!currentGeneration()) return null;
    if (profileObservation !== authObservation) return refreshChangedProfile();
    if (profileError || !profile || !isApprovedProfile(profile) ||
        !['admin', 'manager', 'accountant', 'owner', 'tenant', 'buyer'].includes(profile.role)) {
      delete userContextCache[user.id];
      return null;
    }
    const stillCurrent = async () => {
      if (!currentGeneration()) return false;
      const current = await bounded(supabase.auth.getSession(), 5000, 'Session check timed out');
      return currentGeneration() && !current.error && current.data.session?.user.id === user.id;
    };
    const cached = userContextCache[user.id];
    if (cached && cached.context.role === profile.role && Date.now() - cached.timestamp < CACHE_DURATION) {
      if (!await stillCurrent()) return null;
      return profileObservation === authObservation ? cached.context : refreshChangedProfile();
    }

    // Build user context
    const userContext: UserContext = {
      userId: user.id,
      role: profile.role as UserRole,
      profileType: profile.profile_type,
      isAuthenticated: true,
    };

    // For owners, get their property IDs
    if (profile.role === 'owner') {
      const { data: ownedProperties, error: ownedError } = await boundedRead(signal => supabase
        .from('properties')
        .select('id')
        .eq('owner_id', user.id).abortSignal(signal), 'Property access check timed out');
      if (!currentGeneration() || ownedError) return null;
      
      userContext.ownedPropertyIds = ownedProperties?.map(p => p.id) || [];
    }

    // For tenants, get their rented property IDs with optimized query
    if (profile.role === 'tenant') {
      // Use a more efficient query with proper date handling and timeout
      const now = new Date().toISOString();
      const contractsPromise = supabase
        .from('contracts')
        .select('property_id')
        .eq('tenant_id', user.id)
        .eq('status', 'active')
        .lte('start_date', now)  // Contract has started
        .gte('end_date', now);   // Contract hasn't ended
      
      // Add timeout for this specific query
      const { data: contracts, error: contractsError } = await boundedRead(signal => contractsPromise.abortSignal(signal), 'Tenant contracts query timed out');
      
      if (!currentGeneration() || contractsError) {
        return null;
      } else if (contracts) {
        userContext.rentedPropertyIds = contracts.map(c => c.property_id);
      } else {
        userContext.rentedPropertyIds = [];
      }
    }

    if (!await stillCurrent() || !currentGeneration()) return null;
    if (profileObservation !== authObservation) return refreshChangedProfile();
    // Cache only after confirming the authenticated account has not changed.
    userContextCache[user.id] = {
      context: userContext,
      timestamp: Date.now()
    };

    return userContext;
  } catch (err: any) {
    console.error('[Security] Error getting user context:', err);
    // Do not reference undefined variables here; return null to force re-auth or retry upstream
    return null;
  }
}

/**
 * Clear user context cache (call when user logs out or profile changes)
 */
export function clearUserContextCache(userId?: string): void {
  sessionGeneration++;
  if (userId) {
    delete userContextCache[userId];
  } else {
    userContextCache = {};
  }
}

/**
 * Check if user has access to a specific property
 */
export function hasPropertyAccess(userContext: UserContext, propertyId: string): boolean {
  if (!userContext.isAuthenticated) return false;
  
  // Admins have access to everything
  if (SECURITY_CONFIG.bypassForAdmin && (userContext.role === 'admin' || userContext.role === 'manager')) {
    return true;
  }

  // Owners have access to their properties
  if (userContext.role === 'owner' && userContext.ownedPropertyIds?.includes(propertyId)) {
    return true;
  }

  // Tenants have access to their rented properties
  if (userContext.role === 'tenant' && userContext.rentedPropertyIds?.includes(propertyId)) {
    return true;
  }

  return false;
}

/**
 * Build role-based WHERE clause for Supabase queries
 */
export function buildRoleBasedFilter(userContext: UserContext | null, tableName: string): any {
  if (!userContext?.isAuthenticated || !userContext.userId ||
      !['admin', 'manager', 'accountant', 'owner', 'tenant', 'buyer'].includes(userContext.role)) {
    return { id: { in: [] } };
  }
  if (!SECURITY_CONFIG.enableRoleBasedAccess) return null;

  // Admins bypass all filters
  if (SECURITY_CONFIG.bypassForAdmin && (userContext.role === 'admin' || userContext.role === 'manager')) {
    return null;
  }

  // Accountants have access to financial data and reports only
  if (userContext.role === 'accountant') {
    return buildAccountantFilter(userContext, tableName);
  }

  switch (tableName) {
    case 'properties':
      return buildPropertiesFilter(userContext);
    
    case 'profiles':
      return buildProfilesFilter(userContext);
    
    case 'contracts':
      return buildContractsFilter(userContext);
    
    case 'maintenance_requests':
      return buildMaintenanceFilter(userContext);
    
    case 'vouchers':
      return buildVouchersFilter(userContext);
    
    case 'invoices':
      return buildInvoicesFilter(userContext);
    
    default:
      console.warn(`[Security] No filter defined for table: ${tableName}`);
      return { id: { in: [] } };
  }
}

/**
 * Properties filtering logic
 */
function buildPropertiesFilter(userContext: UserContext): any {
  switch (userContext.role) {
    case 'owner':
      // Owners see only their properties
      return { owner_id: userContext.userId };
    
    case 'tenant':
      // **SECURITY**: Tenants see ONLY their rented properties (licensing restriction)
      return { id: { in: userContext.rentedPropertyIds || [] } };
    
    case 'buyer':
      // **SECURITY**: Buyers see ONLY properties they have relationships with (licensing restriction)
      // TODO: Implement buyer property relationships (reservations, bids, purchases)
      return { id: { in: [] } }; // Empty for now - no properties visible
    
    default:
      return { id: { in: [] } };
  }
}

/**
 * Profiles filtering logic
 */
function buildProfilesFilter(userContext: UserContext): any {
  switch (userContext.role) {
    case 'owner':
      // No related tenant IDs are supplied here; expose only the owner's profile.
      return { id: userContext.userId };
    
    case 'tenant':
      // Tenants see only their own profile
      return { id: userContext.userId };
    
    default:
      return { id: { in: [] } };
  }
}

/**
 * Contracts filtering logic
 */
function buildContractsFilter(userContext: UserContext): any {
  switch (userContext.role) {
    case 'owner':
      // Owners see contracts for their properties
      return { property_id: { in: userContext.ownedPropertyIds || [] } };
    
    case 'tenant':
      // Tenants see only their contracts
      return { tenant_id: userContext.userId };
    
    default:
      return { id: { in: [] } };
  }
}

/**
 * Maintenance requests filtering logic
 */
function buildMaintenanceFilter(userContext: UserContext): any {
  switch (userContext.role) {
    case 'owner':
      // Owners see maintenance requests for their properties
      return { property_id: { in: userContext.ownedPropertyIds || [] } };
    
    case 'tenant':
      // Tenants see only their maintenance requests
      return { tenant_id: userContext.userId };
    
    default:
      return { id: { in: [] } };
  }
}

/**
 * Vouchers filtering logic
 */
function buildVouchersFilter(userContext: UserContext): any {
  switch (userContext.role) {
    case 'owner':
      // Owners see vouchers related to their properties
      return { property_id: { in: userContext.ownedPropertyIds || [] } };
    
    case 'tenant':
      // Tenants see vouchers where they are the tenant
      return { tenant_id: userContext.userId };
    
    default:
      return { id: { in: [] } };
  }
}

/**
 * Invoices filtering logic
 */
function buildInvoicesFilter(userContext: UserContext): any {
  switch (userContext.role) {
    case 'owner':
      // Owners see invoices for their properties
      return { property_id: { in: userContext.ownedPropertyIds || [] } };
    
    case 'tenant':
      // Tenants see only their invoices
      return { tenant_id: userContext.userId };
    
    default:
      return { id: { in: [] } };
  }
}

/**
 * Validate if user can perform an action on a resource
 */
export function validateUserAction(userContext: UserContext | null, action: string, resourceType: string, resourceId?: string): boolean {
  if (!userContext || !userContext.isAuthenticated) {
    console.warn(`[Security] Unauthorized action attempt: ${action} on ${resourceType}`);
    return false;
  }

  // Admins can do everything
  if (SECURITY_CONFIG.bypassForAdmin && (userContext.role === 'admin' || userContext.role === 'manager')) {
    return true;
  }

  // Implement action-specific validation
  switch (action) {
    case 'create_property':
      // Only admins and managers can create properties (owners cannot)
      return false; // Admin/manager already bypassed above

    case 'edit_property':
      // Only admins and managers can edit properties (owners cannot)
      return false; // Admin/manager already bypassed above

    case 'delete_property':
      // Only admins and managers can delete properties (owners cannot)
      return false; // Admin/manager already bypassed above

    case 'create_maintenance_request':
      // Tenants and owners can create maintenance requests for their properties
      return (userContext.role === 'tenant' || userContext.role === 'owner') &&
             resourceId && hasPropertyAccess(userContext, resourceId);

    case 'edit_maintenance_request':
      // Tenants and owners can edit maintenance requests for their properties
      return (userContext.role === 'tenant' || userContext.role === 'owner') &&
             resourceId && hasPropertyAccess(userContext, resourceId);

    case 'delete_maintenance_request':
      // Tenants and owners can delete maintenance requests for their properties
      return (userContext.role === 'tenant' || userContext.role === 'owner') &&
             resourceId && hasPropertyAccess(userContext, resourceId);

    case 'view_financial_reports':
      return userContext.role === 'owner';

    default:
      console.warn(`[Security] Unknown action: ${action}`);
      return false;
  }
}

/**
 * Security middleware for API calls
 */
export function withSecurity<T>(apiFunction: (userContext: UserContext | null, ...args: any[]) => Promise<T>) {
  return async (...args: any[]): Promise<T> => {
    const userContext = await getCurrentUserContext();
    
    return apiFunction(userContext, ...args);
  };
}

/**
 * Apply security filter to a Supabase query
 */
export function applySecurityFilter(query: any, userContext: UserContext | null, tableName: string): any {
  const filter = buildRoleBasedFilter(userContext, tableName);
  
  if (!filter) {
    return query; // No filtering needed
  }

  // Apply the filter to the query
  if (filter.or) {
    // Handle OR conditions
    return query.or(filter.or.map((condition: any) => {
      const key = Object.keys(condition)[0];
      const value = condition[key];
      return `${key}.eq.${value}`;
    }).join(','));
  } else {
    // Handle simple conditions
    const key = Object.keys(filter)[0];
    const value = filter[key];
    
    if (typeof value === 'object' && value.in) {
      return query.in(key, value.in);
    } else {
      return query.eq(key, value);
    }
  }
}

// Export security configuration for testing
export { SECURITY_CONFIG };

/**
 * Accountant filtering logic - accountants can only access financial data and reports
 */
function buildAccountantFilter(userContext: UserContext, tableName: string): any {
  switch (tableName) {
    case 'vouchers':
      // Accountants can see all vouchers for financial reporting
      return null; // No filtering - access to all financial data
    
    case 'invoices':
      // Accountants can see all invoices for financial reporting
      return null; // No filtering - access to all financial data
    
    case 'accounts':
      // Accountants can see all accounts for chart of accounts
      return null; // No filtering - access to all accounts
    
    case 'cost_centers':
      // Accountants can see all cost centers
      return null; // No filtering - access to all cost centers
    
    case 'fixed_assets':
      // Accountants can see all fixed assets for depreciation reports
      return null; // No filtering - access to all assets
    
    case 'utility_payments':
      // Accountants can see all utility payments for billing reports
      return null; // No filtering - access to all utility data
    
    case 'property_metrics':
      // Accountants can see all property metrics for financial analysis
      return null; // No filtering - access to all metrics
    
    case 'budgets':
      // Accountants can see all budgets for financial planning
      return null; // No filtering - access to all budgets
    
    case 'property_transactions':
      // Accountants can see all property transactions for financial reporting
      return null; // No filtering - access to all transactions
    
    case 'rental_payment_schedules':
      // Accountants can see all payment schedules for cash flow analysis
      return null; // No filtering - access to all schedules
    
    default:
      // Accountants cannot access other data (properties, profiles, maintenance, etc.)
      console.warn(`[Security] Accountant access denied to table: ${tableName}`);
      return { id: { in: [] } }; // Return empty result set
  }
} 
